import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, GitCompare, X, Camera, ChevronRight } from "lucide-react";
import { fmtRelative } from "@/lib/format";

function stateTone(state) {
  return {
    up:      "text-emerald-700 bg-emerald-50 border-emerald-300",
    down:    "text-rose-700 bg-rose-50 border-rose-300",
    flat:    "text-muted-foreground bg-muted/40 border-border",
    added:   "text-sky-700 bg-sky-50 border-sky-300",
    removed: "text-amber-700 bg-amber-50 border-amber-300",
  }[state] || "text-muted-foreground bg-muted/40 border-border";
}

export default function PortfolioSnapshotsDialog({ wsId, onClose }) {
  const [snapshots, setSnapshots] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get(`/workspaces/${wsId}/brands/pulse/portfolio-snapshots`)
      .then((r) => setSnapshots(r.data.rows || []))
      .catch((e) => { toast.error(apiErrorMessage(e, "Failed to load snapshots")); setSnapshots([]); });
  };
  useEffect(() => { if (wsId) load(); }, [wsId]);

  const openSnapshot = async (sid) => {
    setSelectedId(sid);
    setLoading(true);
    setDetail(null);
    try {
      const r = await api.get(`/workspaces/${wsId}/brands/pulse/portfolio-snapshots/${sid}?diff_against_current=true`);
      setDetail(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load snapshot detail")); }
    finally { setLoading(false); }
  };

  const del = async (sid) => {
    if (!window.confirm("Delete this portfolio snapshot?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/brands/pulse/portfolio-snapshots/${sid}`);
      toast.success("Snapshot deleted");
      if (selectedId === sid) { setSelectedId(null); setDetail(null); }
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const summary = detail?.diff_summary || {};

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl" data-testid="portfolio-snapshots-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-primary" />
            Portfolio Pulse snapshots
            <Badge variant="outline" className="rounded-none text-[10px]">{snapshots?.length || 0}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3 min-h-[420px]">
          {/* Snapshot list */}
          <div className="border border-border bg-card max-h-[520px] overflow-auto" data-testid="portfolio-snapshots-list">
            {snapshots === null ? (
              <div className="p-4 text-xs text-muted-foreground">Loading…</div>
            ) : snapshots.length === 0 ? (
              <div className="p-4 text-xs text-muted-foreground italic">
                No saved snapshots yet. Save one from the dashboard toolbar.
              </div>
            ) : snapshots.map((s) => (
              <div key={s.id}
                     className={`w-full flex items-center gap-1 border-b border-border ${selectedId === s.id ? "bg-primary/5" : ""}`}>
                <button type="button" onClick={() => openSnapshot(s.id)}
                          className="flex-1 text-left px-3 py-2 hover:bg-muted/40"
                          data-testid={`portfolio-snapshot-row-${s.id}`}>
                  <div className="text-sm font-medium truncate">{s.name}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {fmtRelative(s.created_at)} · avg {s.aggregate?.avg ?? "—"} · {s.aggregate?.total ?? 0} brands
                  </div>
                </button>
                <button type="button" onClick={() => del(s.id)}
                          className="p-2 text-muted-foreground hover:text-rose-600"
                          title="Delete"
                          data-testid={`portfolio-snapshot-del-${s.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          {/* Detail + diff vs current */}
          <div className="border border-border bg-card p-3" data-testid="portfolio-snapshot-detail">
            {!selectedId ? (
              <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                Select a snapshot to see its diff vs the live portfolio.
              </div>
            ) : loading ? (
              <div className="text-xs text-muted-foreground">Loading…</div>
            ) : detail ? (
              <>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-sm font-semibold flex items-center gap-2">
                      <GitCompare className="w-4 h-4 text-primary" /> {detail.name}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Captured {fmtRelative(detail.created_at)} · avg then {detail.aggregate?.avg}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <SumChip tone="text-emerald-700 bg-emerald-50 border-emerald-300" label="Improved" v={summary.improved} />
                    <SumChip tone="text-rose-700 bg-rose-50 border-rose-300" label="Declined" v={summary.declined} />
                    <SumChip tone="text-muted-foreground bg-muted/40 border-border" label="Flat" v={summary.flat} />
                    <SumChip tone="text-sky-700 bg-sky-50 border-sky-300" label="Added" v={summary.added} />
                    <SumChip tone="text-amber-700 bg-amber-50 border-amber-300" label="Removed" v={summary.removed} />
                  </div>
                </div>
                <div className="mt-3 border border-border max-h-[420px] overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Brand</th>
                        <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Then</th>
                        <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Now</th>
                        <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Δ</th>
                        <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Band shift</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.diff || []).map((d) => (
                        <tr key={d.brand_id} className="border-b border-border" data-testid={`portfolio-snapshot-diff-${d.brand_id}`}>
                          <td className="px-2 py-1 text-xs font-medium truncate max-w-[220px]">{d.brand_name}</td>
                          <td className="px-2 py-1 text-right text-xs font-mono">{d.then ?? "—"}</td>
                          <td className="px-2 py-1 text-right text-xs font-mono">{d.now ?? "—"}</td>
                          <td className="px-2 py-1 text-right">
                            <span className={`text-[10px] font-mono border px-1 py-0.5 ${stateTone(d.state)}`}>
                              {d.delta === null || d.delta === undefined ? d.state : `${d.delta > 0 ? "+" : ""}${d.delta}`}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-xs">
                            <span className="text-muted-foreground">{d.band_then || "—"}</span>
                            <ChevronRight className="w-3 h-3 inline mx-0.5 text-muted-foreground" />
                            <span className="font-medium">{d.band_now || "—"}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex justify-end mt-2">
          <Button variant="outline" size="sm" className="rounded-none" onClick={onClose}>
            <X className="w-3.5 h-3.5 mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SumChip({ tone, label, v }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono border px-1.5 py-0.5 ${tone}`}>
      <span className="uppercase text-[9px]">{label}</span>
      <span className="font-bold">{v ?? 0}</span>
    </span>
  );
}
